import { call, put, takeLatest } from 'redux-saga/effects'
import { AsyncAction } from 'src/utils/reduxUtils'
import { ApiService, callClientSecureApi } from './api/callApi'
import { Endpoints } from 'src/constants/endpoints'
import {
  FetchDriver,
  UploadSSN,
  CombinedUploadSSN,
  ManualUploadSSN,
} from 'src/constants/actionTypes'
import { CombinedUploadSSNRequest, ManualUploadSSNRequest } from 'src/interfaces/ssn'
import { captureSentryError } from 'src/utils/sentry'

interface UploadSSNRequest {
  ssn: string
  address: {
    streetAddress: string
    city: string
    state: string
    zipCode: string
  }
  stepId: string
}

function* onUploadSSN(action: AsyncAction<UploadSSNRequest>) {
  const { ssn, address, stepId } = action.payload

  try {
    yield call(callClientSecureApi, Endpoints.UploadSSN, {
      method: 'POST',
      body: { ssn, address, stepId },
      apiService: ApiService.Paraworks,
    })

    yield put(UploadSSN.success())

    yield put(FetchDriver.request())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    captureSentryError(err, {
      message: '[saga.ssn.onUploadSSN] error',
    })

    const errorMessage = err?.payload?.message || 'Failed to upload SSN'

    yield put(UploadSSN.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

function* onCombinedUploadSSN(action: AsyncAction<CombinedUploadSSNRequest>) {
  const { ssn, address, stepId, ssnFile, workAuthorization, workAuthFile } = action.payload

  try {
    const formData = new FormData()

    formData.append('stepId', stepId)
    formData.append('ssn', ssn)
    formData.append('address[streetAddress]', address.streetAddress)
    formData.append('address[city]', address.city)
    formData.append('address[state]', address.state)
    formData.append('address[zipCode]', address.zipCode)
    formData.append('ssnCard', ssnFile)

    if (workAuthorization && workAuthFile) {
      formData.append('workAuthorization', workAuthFile)
    }

    yield call(callClientSecureApi, Endpoints.UploadSSN, {
      method: 'POST',
      apiService: ApiService.Paraworks,
      body: formData,
      resetContentType: true,
      returnResponse: true,
    })

    yield put(CombinedUploadSSN.success())

    yield put(FetchDriver.request())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    captureSentryError(err, {
      message: '[saga.ssn.onCombinedUploadSSN] error',
    })

    const errorMessage = err?.payload?.message || 'Failed to upload SSN'

    yield put(CombinedUploadSSN.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

function* onManualUploadSSN(action: AsyncAction<ManualUploadSSNRequest>) {
  const { ssn, address, stepId, ssnFile, workAuthorization, workAuthFile } = action.payload

  try {
    const formData = new FormData()

    formData.append('stepId', stepId)
    formData.append('ssn', ssn)
    formData.append('address[streetAddress]', address.streetAddress)
    formData.append('address[city]', address.city)
    formData.append('address[state]', address.state)
    formData.append('address[zipCode]', address.zipCode)
    formData.append('ssnCard', ssnFile)

    if (workAuthorization && workAuthFile) {
      formData.append('workAuthorization', workAuthFile)
    }

    yield call(callClientSecureApi, Endpoints.UploadSSN, {
      method: 'POST',
      apiService: ApiService.Paraworks,
      body: formData,
      resetContentType: true,
      returnResponse: true,
    })

    yield put(ManualUploadSSN.success())

    yield put(FetchDriver.request())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    captureSentryError(err, {
      message: '[saga.ssn.onManualUploadSSN] error',
    })

    const errorMessage = err?.payload?.message || 'Failed to upload SSN'

    yield put(ManualUploadSSN.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

export function* ssnSaga() {
  yield takeLatest(UploadSSN.type.REQUEST, onUploadSSN)
  yield takeLatest(CombinedUploadSSN.type.REQUEST, onCombinedUploadSSN)
  yield takeLatest(ManualUploadSSN.type.REQUEST, onManualUploadSSN)
}
